import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import {
  FiArrowLeft,
  FiCalendar,
  FiDownload,
  FiExternalLink,
  FiFile,
  FiFileText,
  FiImage,
  FiRefreshCw,
  FiUser,
} from "react-icons/fi";
import { END_POINT, getJwtToken } from "../../Redux/AdminReducer/action";
import CustomSpinner from "../../Components/CommonComponents/CustomSpinner";
import RightWrapper from "../../Components/CommonComponents/ComponentWrapper";
import { DownloadFile } from "../../UtilityHookFunctions/OtherMongoHelperFunctions";

const fetchItems = async (id, date) => {
  const result = await axios.get(`${END_POINT}/customer/${id}/reports?date=${date}`, {
    headers: { Authorization: getJwtToken() },
  });
  return result?.data;
};

// The backend tags each file with where it came from.
const TYPE_META = {
  profile: { label: "Profile", className: "bg-sky-50 text-sky-700 border-sky-200" },
  external: { label: "Patient upload", className: "bg-violet-50 text-violet-700 border-violet-200" },
  treatment: { label: "Treatment", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

const isSavedPatientId = (value) => /^[0-9a-fA-F]{24}$/.test(value || "");

const isImage = (report) => /^image\//i.test(report?.contentType || "");
const isPdf = (report) => /pdf/i.test(report?.contentType || "") || /\.pdf$/i.test(report?.filename || "");

const FileIcon = ({ report }) => {
  if (isImage(report)) return <FiImage className="h-5 w-5 text-violet-500" />;
  if (isPdf(report)) return <FiFileText className="h-5 w-5 text-rose-500" />;
  return <FiFile className="h-5 w-5 text-slate-400" />;
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (value) => {
  if (!value) return "Date unknown";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "Date unknown"
    : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// The file streams back inline with its real content type, so this opens in a tab.
const viewUrl = (report) => `${END_POINT}/prescription/download/${report?._id}`;

const PatientReports = () => {
  const [reportdate, setreportdate] = useState(new Date().toISOString().slice(0, 10));
  const [reportsData, setReportsData] = useState(null);
  const [error, setError] = useState(null);
  const [activeType, setActiveType] = useState("all");
  const [patient, setPatient] = useState(null);
  const { patientId } = useParams();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setReportsData(null);
    setError(null);
    if (!isSavedPatientId(patientId)) {
      setReportsData([]);
      setError("Save the patient first - reports show up once the patient record exists.");
      return;
    }
    try {
      const data = await fetchItems(patientId, reportdate);
      setReportsData(Array.isArray(data) ? data : []);
    } catch (err) {
      const message = err?.response?.data?.error || err?.response?.data || "Could not load reports";
      setError(typeof message === "string" ? message : "Could not load reports");
      setReportsData([]);
    }
  }, [patientId, reportdate]);

  useEffect(() => {
    load();
  }, [load]);

  // Just for the header — a missing name should never block the list.
  useEffect(() => {
    if (!isSavedPatientId(patientId)) return undefined;
    let cancelled = false;
    axios
      .get(`${END_POINT}/customer/${patientId}`, { headers: { Authorization: getJwtToken() } })
      .then((res) => {
        if (!cancelled) setPatient(res?.data?.data || res?.data || null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const counts = useMemo(() => {
    const base = { all: reportsData?.length || 0, profile: 0, external: 0, treatment: 0 };
    (reportsData || []).forEach((r) => {
      if (base[r?.typeOfDoc] !== undefined) base[r.typeOfDoc] += 1;
    });
    return base;
  }, [reportsData]);

  const visible = useMemo(
    () => (activeType === "all" ? reportsData || [] : (reportsData || []).filter((r) => r?.typeOfDoc === activeType)),
    [reportsData, activeType]
  );

  const patientName = [patient?.firstName, patient?.surname].filter(Boolean).join(" ");

  return (
    <RightWrapper>
      <div className="w-full px-4 py-5 sm:px-6">
        {/* Header */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 active:scale-[0.98]"
                title="Go back"
              >
                <FiArrowLeft className="h-4 w-4" />
                <span>Back</span>
              </button>

              <div className="min-w-0">
                <h1 className="text-lg font-bold text-slate-900 sm:text-xl">Patient Reports</h1>
                <p className="truncate text-xs text-slate-500">
                  {patientName || "Patient"}
                  {patient?.customerId && <span className="ml-1 font-medium text-slate-600">· ID {patient.customerId}</span>}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-slate-50 px-2.5 py-1.5 shadow-sm">
                <FiCalendar className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <input
                  type="date"
                  value={reportdate}
                  onChange={(e) => setreportdate(e.target.value)}
                  className="cursor-pointer bg-transparent text-xs font-medium text-slate-700 outline-none"
                  title="Prescription date"
                />
              </div>

              <button
                onClick={load}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 active:scale-[0.98]"
              >
                <FiRefreshCw className="h-3.5 w-3.5" />
                <span>Refresh</span>
              </button>

              <Link
                to={`/patients/${patientId}/profile`}
                className="inline-flex items-center gap-1.5 rounded-xl bg-sky-600 px-3 py-2 text-xs font-semibold text-white shadow-sm shadow-sky-600/20 transition hover:bg-sky-700 active:scale-[0.98]"
              >
                <FiUser className="h-3.5 w-3.5" />
                <span>Patient profile</span>
              </Link>
            </div>
          </div>

          {/* Type filters */}
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            {[
              { id: "all", label: "All" },
              { id: "profile", label: TYPE_META.profile.label },
              { id: "external", label: TYPE_META.external.label },
              { id: "treatment", label: TYPE_META.treatment.label },
            ].map((chip) => (
              <button
                key={chip.id}
                onClick={() => setActiveType(chip.id)}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition ${
                  activeType === chip.id
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50"
                }`}
              >
                {chip.label}
                <span className={`ml-1.5 ${activeType === chip.id ? "text-slate-300" : "text-slate-400"}`}>{counts[chip.id]}</span>
              </button>
            ))}

            <p className="ml-auto text-[11px] text-slate-400">
              The date filters treatment files. Profile and patient uploads always show.
            </p>
          </div>
        </div>

        {/* List */}
        <div className="mt-4">
          {reportsData === null ? (
            <div className="py-24">
              <CustomSpinner />
            </div>
          ) : error ? (
            <p className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-700">{error}</p>
          ) : visible.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-12 text-center">
              <FiFile className="mx-auto h-8 w-8 text-slate-300" />
              <p className="mt-3 text-sm font-semibold text-slate-700">
                {counts.all === 0 ? "No reports for this patient" : "Nothing in this category"}
              </p>
              <p className="mx-auto mt-1 max-w-sm text-xs leading-relaxed text-slate-500">
                {counts.all === 0
                  ? "Files uploaded against a prescription, the patient record, or sent in by the patient will appear here."
                  : "Try another category, or pick a different prescription date."}
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {visible.map((report) => {
                const meta = TYPE_META[report?.typeOfDoc] || { label: report?.typeOfDoc || "File", className: "bg-slate-50 text-slate-600 border-slate-200" };
                const size = formatSize(report?.length);

                return (
                  <li
                    key={report?._id}
                    className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300 sm:flex-row sm:items-center"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-50">
                        <FileIcon report={report} />
                      </div>

                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-800" title={report?.filename}>
                          {report?.filename || "Untitled file"}
                        </p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                          <span>{formatDate(report?.uploadDate)}</span>
                          {size && <span className="text-slate-300">·</span>}
                          {size && <span>{size}</span>}
                        </p>
                      </div>
                    </div>

                    <span className={`inline-flex shrink-0 items-center self-start rounded-full border px-2.5 py-0.5 text-[11px] font-semibold sm:self-auto ${meta.className}`}>
                      {meta.label}
                    </span>

                    <div className="flex shrink-0 items-center gap-2">
                      <a
                        href={viewUrl(report)}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        <FiExternalLink className="h-3 w-3" />
                        View
                      </a>

                      <button
                        type="button"
                        onClick={() => DownloadFile("prescription/download", report?._id, report?.filename)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800 active:scale-[0.98]"
                      >
                        <FiDownload className="h-3 w-3" />
                        Download
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </RightWrapper>
  );
};

export default PatientReports;
